import React, { useState, useEffect } from 'react';
import { Sparkles, RefreshCw, AlertCircle, TrendingUp, TrendingDown, DollarSign, BarChart3 } from 'lucide-react';

const AIAnalysis = ({ quote, historicalData, symbol }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (quote && historicalData && historicalData.length > 0 && symbol) {
      generateAIAnalysis();
    }
  }, [quote, historicalData, symbol]);

  const generateAIAnalysis = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Prepare data for AI analysis
      const analysisData = {
        symbol,
        currentPrice: quote.price,
        change: quote.change,
        changePercent: quote.changePercent,
        volume: quote.volume,
        high: quote.high,
        low: quote.low,
        open: quote.open,
        previousClose: quote.previousClose,
        latestTradingDay: quote.latestTradingDay,
        recentPrices: historicalData.slice(-10).map(d => ({
          date: d.date,
          close: d.close,
          volume: d.volume,
          high: d.high,
          low: d.low
        }))
      };

      const API_BASE_URL = process.env.NODE_ENV === 'production' 
        ? '/api' 
        : 'http://localhost:3001/api';
      const response = await fetch(`${API_BASE_URL}/ai-analysis`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(analysisData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          window.location.reload();
          return;
        }
        throw new Error('Failed to get AI analysis');
      }

      const result = await response.json();
      setAnalysis(result);
    } catch (err) {
      console.error('AI Analysis error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const retryAnalysis = () => {
    generateAIAnalysis();
  };

  if (loading) {
    return (
      <div className="card loading-skeleton">
        <div className="ai-analysis-header">
          <div className="skeleton-item" style={{height: '1.5rem', width: '50%'}}></div>
          <div className="skeleton-item" style={{height: '1rem', width: '30%'}}></div>
        </div>
        <div className="skeleton-item" style={{height: '6rem', marginBottom: '1rem'}}></div>
        <div className="skeleton-item" style={{height: '4rem', marginBottom: '1rem'}}></div>
        <div className="skeleton-item" style={{height: '3rem'}}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="ai-analysis-header">
          <h3 className="card-title">
            <Sparkles size={20} />
            AI Market Analysis
          </h3>
          <div className="ai-analysis-badge">
            Powered by AI
          </div>
        </div>
        
        <div className="ai-error-state">
          <AlertCircle size={48} />
          <h4>AI Analysis Unavailable</h4>
          <p>{error}</p>
          <button onClick={retryAnalysis} className="retry-button">
            <RefreshCw size={16} />
            Retry Analysis
          </button>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="empty-state">
        <Sparkles className="empty-state-icon" />
        <p>Select a stock to view AI analysis</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="ai-analysis-header">
        <h3 className="card-title">
          <Sparkles size={20} />
          AI Market Analysis
        </h3>
        <div className="ai-analysis-badge">
          Powered by AI
        </div>
      </div>

      {/* AI Summary */}
      <div className="ai-section">
        <h4 className="ai-section-title">
          <Sparkles size={16} />
          AI Summary
        </h4>
        <div className="ai-summary">
          <p className="ai-summary-text">{analysis.summary}</p>
        </div>
      </div>

      {/* Market Sentiment */}
      <div className="ai-section">
        <h4 className="ai-section-title">
          <TrendingUp size={16} />
          Market Sentiment
        </h4>
        <div className="sentiment-container">
          <div className={`sentiment-badge ${analysis.sentiment.overall.toLowerCase()}`}>
            {analysis.sentiment.overall}
          </div>
          <div className="sentiment-score">
            <span className="sentiment-label">Confidence:</span>
            <span className="sentiment-value">{analysis.sentiment.confidence}%</span>
          </div>
        </div>
        <p className="sentiment-reasoning">{analysis.sentiment.reasoning}</p>
      </div>

      {/* Key Factors */}
      <div className="ai-section">
        <h4 className="ai-section-title">
          <BarChart3 size={16} />
          Key Factors
        </h4>
        <div className="factors-list">
          {analysis.keyFactors.map((factor, index) => (
            <div key={index} className={`factor-item ${factor.impact.toLowerCase()}`}>
              <div className="factor-header">
                <span className="factor-title">{factor.factor}</span>
                <span className={`factor-impact ${factor.impact.toLowerCase()}`}>
                  {factor.impact}
                </span>
              </div>
              <p className="factor-description">{factor.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Price Prediction */}
      <div className="ai-section">
        <h4 className="ai-section-title">
          <DollarSign size={16} />
          AI Price Outlook
        </h4>
        <div className="prediction-container">
          <div className="prediction-timeframes">
            {analysis.prediction.timeframes.map((tf, index) => (
              <div key={index} className="prediction-item">
                <span className="prediction-period">{tf.period}</span>
                <span className={`prediction-direction ${tf.direction.toLowerCase()}`}>
                  {tf.direction === 'UP' ? '↗' : tf.direction === 'DOWN' ? '↘' : '→'} {tf.range}
                </span>
              </div>
            ))}
          </div>
          <p className="prediction-reasoning">{analysis.prediction.reasoning}</p>
        </div>
      </div>

      {/* Risk Assessment */}
      <div className="ai-section">
        <h4 className="ai-section-title">
          <AlertCircle size={16} />
          Risk Assessment
        </h4>
        <div className="risk-container">
          <div className={`risk-level ${analysis.riskAssessment.level.toLowerCase()}`}>
            {analysis.riskAssessment.level} Risk
          </div>
          <div className="risk-factors">
            {analysis.riskAssessment.factors.map((risk, index) => (
              <span key={index} className="risk-factor">{risk}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="ai-disclaimer">
        <Sparkles size={14} />
        <span>
          AI analysis generated using market data and machine learning models. 
          Predictions are speculative and should not be considered as financial advice.
        </span>
      </div>
    </div>
  );
};

export default AIAnalysis;